import React, { useEffect } from 'react';
import { Box, BoxProps, forwardRef } from '@chakra-ui/react';
import {
  ColorMode,
  callAllHandlers,
  useColorMode,
} from '@allergan-data-labs/alle-elements-core';
import { getIconWrapperStyles } from './form-label-icon.theme';

export type FormLabelIconProps = {
  /**
   * This prop is used to determine if the icon is interactive or not. If true, the icon will change the cursor to a pointer and will be focusable.
   * If `onHoverIcon` or `onClickIcon` is provided, the icon will change on hover or click.
   * @default false
   */
  isInteractive?: boolean;
  /**
   * This prop is used to determine if the icon is active or not. Use this prop to change the state of the icon from outside the component.
   * This is useful to use it for example along with a modal component
   * @default false
   */
  isActive?: boolean;
  /**
   * The color mode of the component.
   * @default light
   */
  colorMode?: ColorMode;

  /**
   *  Icon to be displayed.
   */
  defaultIcon: React.ReactElement;

  /**
   * Icon to be displayed on hover. Do not use `onHoverIcon` and `onClickIcon` together to avoid unexpected behavior.
   * @default null
   */
  onHoverIcon?: React.ReactElement;

  /**
   * Icon to be displayed on click. Do not use `onHoverIcon` and `onClickIcon` together to avoid unexpected behavior.
   * @default null
   */
  onClickIcon?: React.ReactElement;
} & Omit<BoxProps, 'children'>;

export const FormLabelIcon = forwardRef<FormLabelIconProps, 'div'>(
  (
    {
      onClick,
      onMouseOver,
      onMouseOut,
      onKeyDown,
      onFocusCapture,
      onBlurCapture,
      defaultIcon,
      onHoverIcon,
      onClickIcon,
      isActive = false,
      colorMode: propColorMode,
      isInteractive = false,
      ...rest
    }: FormLabelIconProps,
    ref
  ) => {
    const { colorMode } = useColorMode(propColorMode);

    const [icon, setIcon] = React.useState<React.ReactElement>(defaultIcon);
    const [iconIsActive, setIconIsActive] = React.useState(isActive);

    useEffect(() => {
      setIconIsActive(isActive);
    }, [isActive]);

    useEffect(() => {
      if (onClickIcon) {
        setIcon(!iconIsActive ? defaultIcon : onClickIcon);
      }
    }, [onClickIcon, iconIsActive, defaultIcon]);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
      if (isInteractive) {
        event.preventDefault();
        event.stopPropagation();
        if (onClickIcon) {
          setIcon(iconIsActive ? defaultIcon : onClickIcon);
          setIconIsActive(!iconIsActive);
        }
      }
    };

    const handleOver = () => {
      if (isInteractive && onHoverIcon) {
        setIcon(onHoverIcon);
      }
    };

    const handleOut = () => {
      if (isInteractive && onHoverIcon) {
        setIcon(defaultIcon);
      }
    };

    const onKeyDownHandler = (event: React.KeyboardEvent<HTMLElement>) => {
      if ((event.key === 'Enter' || event.code === 'Space') && onClickIcon) {
        event.preventDefault();
        if (iconIsActive) {
          setIcon(defaultIcon);
        } else {
          setIcon(onClickIcon);
        }
        setIconIsActive(!iconIsActive);
      }
    };

    if (Boolean(onHoverIcon) && Boolean(onClickIcon)) {
      console.warn(
        'Do not use `onHoverIcon` and `onClickIcon` together to avoid unexpected behavior.'
      );
    }

    return (
      <Box
        ref={ref}
        sx={getIconWrapperStyles(
          isInteractive,
          colorMode,
          Boolean(onHoverIcon),
          iconIsActive
        )}
        onClick={callAllHandlers(onClick, handleClick)}
        onMouseOver={callAllHandlers(onMouseOver, handleOver)}
        onMouseOut={callAllHandlers(onMouseOut, handleOut)}
        onFocusCapture={callAllHandlers(onFocusCapture, handleOver)}
        onBlurCapture={callAllHandlers(onBlurCapture, handleOut)}
        onKeyDown={callAllHandlers(onKeyDown, onKeyDownHandler)}
        tabIndex={isInteractive ? 0 : undefined}
        {...rest}
      >
        {icon}
      </Box>
    );
  }
);
